import {Button} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {unixTimestampMSFromUTCString} from '@wandb/weave/common/util/time';
import React, {useCallback, useState} from 'react';
import {useHistory} from 'react-router';
import ReactTimeago from 'react-timeago';

import {
  useInsertGalleryDiscussionMutation,
  useUpdateAccessTokenProjectsMutation,
} from '../generated/graphql';
import {useLoadedReportContext} from '../pages/ReportPage/LoadedReportContext';
import {useReportPageContext} from '../pages/ReportPage/ReportPageContext';
import {useReportState} from '../pages/ReportPage/store';
import * as Analytics from '../services/analytics';
import {useApolloClient, useSelector} from '../state/hooks';
import {useViewer} from '../state/viewer/hooks';
import * as ViewApi from '../state/views/api';
import {LoadResultType as LoadedView} from '../state/views/api';
import * as ViewNormalize from '../state/views/normalize';
import * as ViewSelectors from '../state/views/selectors';
import {
  DEFAULT_LANGUAGE,
  getAncestorTagIDSetForReport,
  getRecacheURLsForTags,
  ReportIDWithTagIDs,
  triggerPrerenderRecacheForURLs,
  useGallerySpec,
  useGalleryTags,
  useTrackReportActivity,
} from '../util/gallery';
import {
  getProjectSpecifierKey,
  getProjectSpecifierMap,
  useReportProjects,
} from '../util/report/useReportProjects';
import {
  isOnGalleryDiscussionEdit,
  isOnGalleryDiscussionView,
  isOnGalleryPostEdit,
  isOnGalleryPostView,
} from '../util/urls';
import * as Urls from '../util/urls';
import ConflictWarningModal from './ConflictWarningModal';
import {TeamMemberRole} from './MembersTable/TeamMembersTable/utils';
import WBModal from './WBModal';

const SaveDraftTrigger = () => {
  const viewer = useViewer();
  const client = useApolloClient();
  const history = useHistory();
  const tags = useGalleryTags();
  const {reportViewRef, view} = useLoadedReportContext();
  const views = useSelector(ViewSelectors.getViews);
  const reportID = view.id;
  if (reportID == null) {
    throw new Error('invalid state');
  }
  const viewPartRef = view.partRef;
  if (viewPartRef == null) {
    throw new Error('View in redux missing part ref');
  }

  const {
    projectName,
    entityName,
    teamMemberInfo: {foundTeamMember},
    isPublicProject,
  } = useReportPageContext();
  const isSyncingToRedux = useReportState(s => s.isSyncingToRedux);

  const [saving, setSaving] = useState(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const cancelSaving = useCallback(() => {
    setWarningOpen(false);
    setSaving(false);
  }, []);
  const [parent, setParent] = useState<LoadedView | null>(null);

  const [updateAccessTokenProjects] = useUpdateAccessTokenProjectsMutation();
  const [insertGalleryDiscussion] = useInsertGalleryDiscussionMutation();

  const {gallerySpec, refetchGallerySpec} = useGallerySpec();

  const [thanksModalOpen, setThanksModalOpen] = useState(false);

  const projectsIncluded = useReportProjects(reportViewRef);

  const needAccessTokenUpdate = (parentView: LoadedView): boolean => {
    if (
      parentView.accessTokens == null ||
      parentView.accessTokens.length === 0
    ) {
      return false;
    }
    const accessTokenProjects = parentView.accessTokens[0].projects;
    if (projectsIncluded.length !== accessTokenProjects.length) {
      return true;
    }

    const projectSpecifierMap = getProjectSpecifierMap(projectsIncluded);
    return accessTokenProjects.some(
      p =>
        projectSpecifierMap[
          getProjectSpecifierKey({
            entityName: p.entityName,
            projectName: p.name,
          })
        ] == null
    );
  };

  const track = useTrackReportActivity(view.id);

  if (viewer == null) {
    return null;
  }

  const saveToMaster = async (parentView: LoadedView) => {
    if (parentView.id == null) {
      throw new Error(`null parentView.id`);
    }
    if (view.project == null && view.user.id === viewer.id) {
      if (isOnGalleryPostView() || isOnGalleryPostEdit()) {
        track('triggerSavePostEdits', view.id ?? null);
      }
      if (isOnGalleryDiscussionView() || isOnGalleryDiscussionEdit()) {
        track('triggerSaveDiscussionEdits', view.id ?? null);
      }
    }

    await ViewApi.save(client, {
      id: view.parentId,
      type: 'runs',
      displayName: view.displayName,
      description: view.description,
      spec: ViewNormalize.denormalize(views.parts, viewPartRef, {
        includeRefs: false,
      }),
      project: view.project,
      previewUrl: view.previewUrl,
      coverUrl: view.coverUrl,
    });
    await ViewApi.deleteView(client, reportID);

    if (needAccessTokenUpdate(parentView)) {
      const token = parentView.accessTokens?.[0]?.token;
      if (token != null) {
        await updateAccessTokenProjects({
          variables: {
            token,
            projects: projectsIncluded,
          },
        });
      }
    }

    const recacheURLs = [
      `https://wandb.ai${Urls.reportView({
        entityName: parentView.project?.entityName ?? parentView.entityName,
        projectName: parentView.project?.name,
        reportID: parentView.id,
        reportName: parentView.displayName,
      })}`,
    ];
    const fcReport = gallerySpec.reportIDsWithTagV2IDs.find(
      r => r.id === parentView.id
    );
    if (fcReport != null) {
      const tagByID = new Map(gallerySpec.tagsV2.map(t => [t.id, t]));
      const tagsToUpdateSet = getAncestorTagIDSetForReport(tagByID, fcReport);
      const tagsToUpdate = gallerySpec.tagsV2.filter(t =>
        tagsToUpdateSet.has(t.id)
      );
      const tagRecacheURLs = await getRecacheURLsForTags(tagsToUpdate);
      recacheURLs.push(...tagRecacheURLs);
    }
    await triggerPrerenderRecacheForURLs(recacheURLs);

    history.push(
      Urls.reportView({
        entityName: parentView.project?.entityName ?? parentView.entityName,
        projectName: parentView.project?.name,
        reportID: parentView.id,
        reportName: view.displayName,
      })
    );
  };

  const isViewOnlyTeamMember =
    foundTeamMember?.role === TeamMemberRole.VIEW_ONLY;
  const isDisabled =
    isViewOnlyTeamMember ||
    saving || // user clicked "publish" (explicit save)
    view.saving || // draft is auto-saving (background save)
    isSyncingToRedux; // user edits are being synced to redux; must wait for this to finish before we can save

  const isReportDraft = view.parentId == null;
  const isGalleryPostEdit = isOnGalleryPostEdit();

  const trackReportSubmitted = () => {
    const reportType: Analytics.ReportType = isReportDraft
      ? 'report draft'
      : isGalleryPostEdit
        ? 'gallery post edit'
        : 'published report';

    Analytics.reportSubmitted({
      viewId: view.id,
      entityName,
      projectName,
      reportType,
      isPublicProject,
    });
  };

  const onPublish = async () => {
    if (saving) {
      return;
    }
    setSaving(true);

    if (view.parentId == null) {
      if (isOnGalleryPostView() || isOnGalleryPostEdit()) {
        track('clickSubmitPost', view.id ?? null);
      }
      if (isOnGalleryDiscussionView() || isOnGalleryDiscussionEdit()) {
        track('clickSubmitDiscussion', view.id ?? null);
      }
    }

    if (view.parentId == null) {
      const spec = ViewNormalize.denormalize(views.parts, viewPartRef, {
        includeRefs: false,
      });
      await ViewApi.save(client, {
        id: view.id,
        type: 'runs',
        displayName: view.displayName,
        description: view.description,
        spec,
        project: view.project,
        previewUrl: view.previewUrl,
        coverUrl: view.coverUrl,
      });
      if (isOnGalleryPostView() || isOnGalleryPostEdit()) {
        if (view.id == null) {
          throw new Error(`attempting to publish view with null id`);
        }
        const tag = tags.find(t => t.id === 'ir6df9nxe');
        if (tag == null) {
          throw new Error(`unable to find posts tag`);
        }
        const entrySpec: ReportIDWithTagIDs = {
          id: view.id,
          tagIDs: [tag.id],
          authors: (spec as any).authors ?? [],
          addedAt: new Date().toJSON(),
          language: DEFAULT_LANGUAGE,
          pending: true,
        };
        await insertGalleryDiscussion({
          variables: {
            spec: JSON.stringify(entrySpec),
            reportID: view.id,
            sendEmail: true,
          },
        });
        await refetchGallerySpec();
        setThanksModalOpen(true);
        return;
      }
      if (isOnGalleryDiscussionView() || isOnGalleryDiscussionEdit()) {
        if (view.id == null) {
          throw new Error(`attempting to publish view with null id`);
        }
        const tag = tags.find(t => t.id === '90vhuh15v');
        if (tag == null) {
          throw new Error(`unable to find discussions tag`);
        }
        const entrySpec: ReportIDWithTagIDs = {
          id: view.id,
          tagIDs: [tag.id],
          authors: (spec as any).authors ?? [],
          addedAt: new Date().toJSON(),
          language: DEFAULT_LANGUAGE,
        };
        await insertGalleryDiscussion({
          variables: {
            spec: JSON.stringify(entrySpec),
            reportID: view.id,
          },
        });
        await refetchGallerySpec();
      }
      history.push(
        view.project != null
          ? Urls.reportView({
              entityName: view.project.entityName,
              projectName: view.project.name,
              reportID,
              reportName: view.displayName,
            })
          : Urls.reportGallery({
              tag:
                isOnGalleryDiscussionView() || isOnGalleryDiscussionEdit()
                  ? `forum`
                  : `posts`,
            })
      );
    } else {
      const loadedParent = await ViewApi.loadSingleViewById(
        client,
        view.parentId,
        'no-cache'
      );
      setParent(loadedParent);
      if (
        unixTimestampMSFromUTCString(loadedParent.updatedAt) >
        unixTimestampMSFromUTCString(view.createdAt)
      ) {
        setWarningOpen(true);
      } else {
        saveToMaster(loadedParent);
      }
    }

    trackReportSubmitted();
  };

  return (
    <>
      <Button
        data-test="save-report-button"
        isDisabled={isDisabled}
        startIcon={!isReportDraft ? 'checkmark-alt' : undefined}
        endIcon={isReportDraft ? 'export-share-upload' : undefined}
        onPress={onPublish}>
        {isGalleryPostEdit
          ? 'Publish post'
          : isReportDraft
            ? 'Publish to project'
            : 'Publish changes'}
      </Button>
      {parent != null && warningOpen && (
        <ConflictWarningModal
          onOverwrite={() => saveToMaster(parent)}
          onCancel={cancelSaving}>
          <p style={{marginBottom: 24}}>
            This report was modified{' '}
            <ReactTimeago date={parent.updatedAt} live={false} /> by{' '}
            {parent.updatedBy?.username}.{' '}
            <TargetBlank
              href={Urls.reportView({
                entityName: view.project?.entityName ?? view.entityName,
                projectName: view.project?.name,
                reportID: view.parentId as string,
                reportName: view.displayName,
              })}>
              View the report
            </TargetBlank>{' '}
            to see their changes and potentially resolve conflicts.
          </p>
        </ConflictWarningModal>
      )}
      {thanksModalOpen && (
        <WBModal
          open
          header=""
          primaryAction={{
            content: 'Go back to Fully Connected',
            onClick: () => history.push(Urls.reportGallery({tag: 'posts'})),
          }}>
          <p>
            Thank you for your submission! We’ll review your submission and
            contact you with any suggestions before publishing.
          </p>
        </WBModal>
      )}
    </>
  );
};

export default SaveDraftTrigger;
